import * as React from 'react';
import PropTypes from 'prop-types';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import { Avatar, Badge, useMediaQuery, useTheme } from '@mui/material';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { set_user } from 'redux/reducer/user';
import { useDispatch, useSelector } from 'react-redux';
import { set_message } from 'redux/reducer/message';
import { History, Logout, Notifications, Storefront, Person, Dashboard } from '@mui/icons-material';
import LogoImg from 'assets/xgainTM.png';
import { set_unread_notification } from 'redux/reducer/user';
import getSubscriptionLabel, { getMembershipAccentColor } from 'utils/getSubscriptionLabel';
import DashboardNavLinks from 'component/DashboardNav/DashboardNavLinks';
import DashboardAddAssetButton from 'component/DashboardNav/DashboardAddAssetButton';
import AccountSwitcher from 'component/AccountSwitcher';
import { clearActiveAccountId } from 'utils/accountContext';
import { clearLocalStoragePreservingWalkthrough } from 'utils/onboardingWalkthrough';

function DrawerAppBar() {
  const { user, plan, p_and_l, unread_notification, isAdmin } = useSelector(state => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [menuAnchor, setMenuAnchor] = React.useState(null);

  React.useEffect(() => {
    axios.get('/api/v1/user/unread-notification')
      .then(res => dispatch(set_unread_notification(res.data.count)))
      .catch(err => console.log(err));
  }, [location, dispatch]);

  const handleLogout = () => {
    axios.post('/api/v1/user/logout')
      .then(() => {
        clearLocalStoragePreservingWalkthrough();
        clearActiveAccountId();
        dispatch(set_user(null));
        navigate('/');
      })
      .catch(() => dispatch(set_message('Ooops! Something went wrong')));
  };

  const subscriptionLabel = React.useMemo(() => getSubscriptionLabel(plan), [plan]);
  const membershipColor = React.useMemo(
    () => getMembershipAccentColor(plan?.membershipStatus),
    [plan?.membershipStatus],
  );

  const isAccountPage = location.pathname.startsWith('/dashboard/my-account');
  const menuOpen = Boolean(menuAnchor);

  const closeMenu = () => setMenuAnchor(null);

  const handleAvatarClick = (event) => {
    if (isMobile) {
      setMenuAnchor(event.currentTarget);
      return;
    }
    navigate('/dashboard/my-account');
  };

  const goTo = (path) => {
    closeMenu();
    navigate(path);
  };

  const isAdminDashboard = location.pathname.startsWith('/dashboard/admin');

  return (
    <Box sx={{ display: 'flex', marginBottom: 7 }}>
      <CssBaseline />
      <AppBar component="nav">
        <Toolbar
          disableGutters
          sx={{
            px: { xs: 1, md: 2 },
            minHeight: { xs: 56, md: 64 },
            gap: { xs: 0.5, md: 1 },
          }}
        >
          <Link
            style={{ display: 'flex', textDecoration: 'none', color: '#fff', flexShrink: 0 }}
            to="/dashboard/main"
          >
            <Box component="img" height={{ xs: 20, md: 25 }} src={LogoImg} alt="XGAIN" />
          </Link>

          <AccountSwitcher />

          {isAdmin && (
            <Button
              component={Link}
              to="/dashboard/admin"
              variant="text"
              sx={{
                color: isAdminDashboard ? '#95F205' : '#fff',
                textTransform: 'none',
                fontSize: { xs: 12, md: 14 },
                fontWeight: isAdminDashboard ? 700 : 500,
                minWidth: 'auto',
                px: { xs: 1, md: 1.5 },
                flexShrink: 0,
              }}
            >
              Dashboard
            </Button>
          )}

          {subscriptionLabel && (
            <Typography
              component="span"
              sx={{
                display: { xs: 'none', md: 'inline' },
                color: membershipColor,
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                borderLeft: '1px solid rgba(255,255,255,0.2)',
                pl: 1.5,
              }}
            >
              {subscriptionLabel}
            </Typography>
          )}

          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              ml: 3,
              gap: 0.25,
            }}
          >
            <DashboardNavLinks variant="header" />
            <DashboardAddAssetButton variant="header" />
          </Box>

          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Button
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                mr: 0.5,
                minWidth: 'auto',
                color: location.pathname.startsWith('/dashboard/activity') ? '#95F205' : '#fff',
              }}
              aria-label="Activity history"
            >
              <Link to="/dashboard/activity" style={{ display: 'flex', color: 'inherit' }}>
                <History />
              </Link>
            </Button>

            <IconButton
              aria-label="Notifications"
              component={Link}
              to="/dashboard/notification"
              color="inherit"
              sx={{ mr: { xs: 0, md: 0.5 } }}
            >
              <Badge
                sx={{
                  '& .MuiBadge-badge': {
                    color: '#fff',
                    backgroundColor: 'red',
                  },
                }}
                badgeContent={unread_notification || null}
              >
                <Notifications sx={{ color: '#fff' }} />
              </Badge>
            </IconButton>

            <Button
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                mr: 0.5,
                minWidth: 'auto',
                color: '#fff',
              }}
              aria-label="Merch store"
            >
              <a
                href="https://xgain-store.creator-spring.com/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'flex', color: 'inherit' }}
              >
                <Storefront />
              </a>
            </Button>

            <IconButton
              size="large"
              aria-label="Account menu"
              aria-controls={menuOpen ? 'mobile-account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={menuOpen ? 'true' : undefined}
              onClick={handleAvatarClick}
              color="inherit"
              sx={{
                outline: isAccountPage ? '2px solid #95F205' : 'none',
                outlineOffset: 2,
              }}
            >
              <Avatar sx={{ width: 36, height: 36, bgcolor: p_and_l < 0 ? 'red' : 'green', fontSize: 14 }}>
                {user?.name?.charAt(0)}
                {user?.name?.charAt(1)}
              </Avatar>
            </IconButton>

            <IconButton
              aria-label="Log out"
              onClick={handleLogout}
              color="inherit"
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                color: '#fff',
                ml: 0.5,
              }}
            >
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        id="mobile-account-menu"
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
            bgcolor: '#1c1e28',
            color: '#fff',
            border: '1px solid #2a2d3a',
          },
        }}
      >
        {subscriptionLabel && (
          <MenuItem disabled sx={{ opacity: 1, color: membershipColor, fontWeight: 600, fontSize: 13 }}>
            {subscriptionLabel}
          </MenuItem>
        )}
        {subscriptionLabel && <Divider sx={{ borderColor: '#2a2d3a' }} />}
        <MenuItem onClick={() => goTo('/dashboard/my-account')}>
          <ListItemIcon><Person fontSize="small" sx={{ color: '#95F205' }} /></ListItemIcon>
          My account
        </MenuItem>
        {isAdmin && (
          <MenuItem onClick={() => goTo('/dashboard/admin')}>
            <ListItemIcon><Dashboard fontSize="small" sx={{ color: '#95F205' }} /></ListItemIcon>
            Dashboard
          </MenuItem>
        )}
        <MenuItem onClick={() => goTo('/dashboard/activity')}>
          <ListItemIcon><History fontSize="small" sx={{ color: '#95F205' }} /></ListItemIcon>
          Activity
        </MenuItem>
        <MenuItem
          component="a"
          href="https://xgain-store.creator-spring.com/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeMenu}
        >
          <ListItemIcon><Storefront fontSize="small" sx={{ color: '#95F205' }} /></ListItemIcon>
          Merch
        </MenuItem>
        <Divider sx={{ borderColor: '#2a2d3a' }} />
        <MenuItem
          onClick={() => {
            closeMenu();
            handleLogout();
          }}
        >
          <ListItemIcon><Logout fontSize="small" sx={{ color: '#f87171' }} /></ListItemIcon>
          Log out
        </MenuItem>
      </Menu>
    </Box>
  );
}

DrawerAppBar.propTypes = {
  window: PropTypes.func,
};

export default DrawerAppBar;
